import { SubtitleTrack } from '@src/queries/media/api';

export interface VideoSubtitleTrack {
  src: string;
  srclang: string;
  format?: string;
}

export interface DisplaySubtitleTrack extends VideoSubtitleTrack {
  sourceIndex: number;
  label: string;
  isOriginal: boolean;
}

const LANGUAGE_ALIASES: Record<string, string> = {
  eng: 'en', fre: 'fr', fra: 'fr', ger: 'de', deu: 'de', spa: 'es', ita: 'it',
  por: 'pt', rus: 'ru', jpn: 'ja', kor: 'ko', chi: 'zh', zho: 'zh', dut: 'nl',
  nld: 'nl', ara: 'ar', hin: 'hi',
};

export const normalizeSubtitleLanguage = (language: string | undefined): string | undefined => {
  const base = language?.trim().replace(/_/g, '-').toLowerCase().split('-')[0];
  if (!base) return undefined;
  const normalized = LANGUAGE_ALIASES[base] || base;
  try {
    const displayName = new Intl.DisplayNames([normalized], { type: 'language' }).of(normalized);
    if (!displayName || displayName.toLowerCase() === normalized || displayName.toLowerCase() === 'root') {
      return undefined;
    }
    return normalized;
  } catch {
    return undefined;
  }
};

export const mapSubtitleTracks = (tracks: SubtitleTrack[] | undefined): VideoSubtitleTrack[] => (
  (tracks || []).filter((track) => (
    typeof track?.url === 'string' && track.url.trim().length > 0
    && typeof track.language === 'string' && track.language.trim().length > 0
  )).map((track) => ({
    src: track.url,
    srclang: track.language,
    format: track.type,
  }))
);

export const isSrtSubtitle = (src: string, format?: string): boolean => {
  const explicitFormat = format?.trim().toLowerCase();
  if (explicitFormat === 'srt') return true;
  if (explicitFormat === 'vtt') return false;
  return /\.srt(?:[?#]|$)/i.test(src);
};

/** Detect SRT from its cue structure when API URLs omit a file extension. */
export const isSrtContent = (input: string): boolean => {
  const content = input.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n').trimStart();
  if (/^WEBVTT(?:\s|$)/i.test(content)) return false;
  return /(?:^|\n)\s*(?:\d+\s*\n)?\d{2}:\d{2}:\d{2}[,.]\d{3}\s*-->\s*\d{2}:\d{2}:\d{2}[,.]\d{3}/.test(content);
};

export const shouldConvertSubtitleToVtt = (src: string, format: string | undefined, content: string): boolean => (
  isSrtContent(content)
  || (!/^\uFEFF?\s*WEBVTT(?:\s|$)/i.test(content) && isSrtSubtitle(src, format))
);

export const srtToVtt = (input: string): string => {
  const blocks = input
    .replace(/^\uFEFF/, '')
    .replace(/\r\n?/g, '\n')
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => {
      const lines = block.split('\n');
      const content = /^\s*\d+\s*$/.test(lines[0]) ? lines.slice(1) : lines;
      return content.join('\n').replace(/(\d{2}:\d{2}:\d{2}),(\d{3})/g, '$1.$2');
    });
  return `WEBVTT\n\n${blocks.join('\n\n')}\n`;
};

export const englishLanguageName = (language: string): string => {
  const normalized = normalizeSubtitleLanguage(language);
  if (!normalized) return language.trim().toUpperCase() || 'Unknown';
  try {
    const englishName = new Intl.DisplayNames(['en'], { type: 'language' }).of(normalized) || normalized;
    const firstCharacter = Array.from(englishName)[0];
    return firstCharacter
      ? `${firstCharacter.toLocaleUpperCase('en')}${englishName.slice(firstCharacter.length)}`
      : englishName;
  } catch {
    return language.trim().toUpperCase() || 'Unknown';
  }
};

/** Sort menu labels without changing the API track order used by player selections. */
export const sortSubtitleTracksForDisplay = (
  tracks: VideoSubtitleTrack[],
  audioLanguage?: string,
): DisplaySubtitleTrack[] => {
  const originalLanguage = normalizeSubtitleLanguage(audioLanguage);
  return tracks.map((track, sourceIndex) => {
    const isOriginal = Boolean(
      originalLanguage && normalizeSubtitleLanguage(track.srclang) === originalLanguage,
    );
    const languageName = englishLanguageName(track.srclang) || `Track ${sourceIndex + 1}`;
    return {
      ...track,
      sourceIndex,
      isOriginal,
      label: isOriginal ? `${languageName} (Original)` : languageName,
    };
  }).sort((a, b) => {
    if (a.isOriginal !== b.isOriginal) return a.isOriginal ? -1 : 1;
    return a.label.localeCompare(b.label, 'en') || a.sourceIndex - b.sourceIndex;
  });
};
